import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronDown, ChevronRight, History, MessageSquareText, Plus, Send, ShieldAlert, Wrench, X } from 'lucide-react';
import type { AiConversation, AiConversationView, AiMessage, AiSubjectType } from '@itr/shared';
import { formatRelative } from '@itr/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Drawer, useFetch, useToast } from './ui';

/**
 * Multi-turn AI chat — additive to `AiAnalysisPanel`'s one-shot Diagnose.
 *
 * Bound to a subject (alert/ticket/resource) it resumes the most recent
 * conversation for that subject by default, but the operator can open a new
 * topic or pick an older one from history at any time — a resource often
 * accumulates several unrelated incidents over time, and auto-resuming the
 * latest one forever would bury the rest. With no subject it is a standalone
 * chat with the same New topic / History controls. Pending write actions
 * render as an inline Approve/Deny card — the server re-checks permission on
 * click regardless of what the UI shows, so disabling the buttons here is
 * purely a courtesy, not enforcement.
 */
export function AiChatPanel({ subjectType, subjectId }: { subjectType?: AiSubjectType; subjectId?: string }) {
  const toast = useToast();
  const { can } = useAuth();
  const bound = Boolean(subjectType && subjectId);

  const [conversationId, setConversationId] = useState<string | null>(null);
  const [view, setView] = useState<AiConversationView | null>(null);
  const [loadingView, setLoadingView] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // Set by "New topic" so the auto-resume effect below does not immediately
  // snap back to the latest conversation just because conversationId is null.
  const [startingNew, setStartingNew] = useState(false);

  const status = useFetch(() => api.get<{ configured: boolean }>('/api/ai/status'), []);
  const recent = useFetch(
    () => (bound ? api.get<AiConversation[]>('/api/ai/conversations', { subjectType, subjectId, limit: 20 }) : api.get<AiConversation[]>('/api/ai/conversations', { limit: 20 })),
    [subjectType, subjectId],
  );

  // Subject changed (e.g. navigated to a different resource) — clean slate.
  useEffect(() => {
    setConversationId(null);
    setView(null);
    setStartingNew(false);
    setShowHistory(false);
  }, [subjectType, subjectId]);

  // Bound panels auto-resume the latest conversation for this subject,
  // unless the operator explicitly started a new topic or already picked one.
  useEffect(() => {
    if (bound && !conversationId && !startingNew && recent.data?.[0]) setConversationId(recent.data[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bound, recent.data, startingNew]);

  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;
    setLoadingView(true);
    api
      .get<AiConversationView>(`/api/ai/conversations/${conversationId}`)
      .then((v) => !cancelled && setView(v))
      .catch(() => {})
      .finally(() => !cancelled && setLoadingView(false));
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  const send = async () => {
    const message = input.trim();
    if (!message) return;
    setSending(true);
    try {
      const v = conversationId
        ? await api.post<AiConversationView>(`/api/ai/conversations/${conversationId}/messages`, { message })
        : await api.post<AiConversationView>('/api/ai/conversations', { subjectType, subjectId, message });
      setView(v);
      setConversationId(v.conversation.id);
      setStartingNew(false);
      setInput('');
      recent.reload();
    } catch (err) {
      toast('Message failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setSending(false);
    }
  };

  const startNewTopic = () => {
    setConversationId(null);
    setView(null);
    setInput('');
    setStartingNew(true);
    setShowHistory(false);
  };

  const selectConversation = (id: string) => {
    setConversationId(id);
    setStartingNew(false);
    setShowHistory(false);
  };

  const decide = async (messageId: string, decision: 'approve' | 'deny') => {
    if (!conversationId) return;
    setDecidingId(messageId);
    try {
      const v = await api.post<AiConversationView>(`/api/ai/conversations/${conversationId}/actions/${messageId}/decision`, { decision });
      setView(v);
    } catch (err) {
      toast('Could not record the decision', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setDecidingId(null);
    }
  };

  // Undefined while `status` is still loading — treated as configured so the
  // button doesn't flash a "not configured" state on every page load.
  const configured = status.data?.configured ?? true;

  const messages = view?.messages ?? [];
  const resultByToolUseId = new Map(messages.filter((m) => m.blockType === 'tool_result' && m.toolUseId).map((m) => [m.toolUseId, m]));

  // Standalone chat shows its list by default on the empty entry screen, same
  // as before this panel could also toggle history — everywhere else (bound,
  // or once a standalone conversation is active) it's opt-in via the button.
  const historyVisible = showHistory || (!bound && !conversationId);

  const historySection = historyVisible && (
    <div className="col" style={{ gap: 6, marginBottom: 12, paddingBottom: 12, borderBottom: 'var(--itr-border-w) solid var(--itr-border)' }}>
      <span className="subtle" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
        History
      </span>
      {recent.data && recent.data.length > 0 ? (
        recent.data.map((c) => (
          <button
            key={c.id}
            className="row"
            onClick={() => selectConversation(c.id)}
            style={{
              gap: 8,
              background: c.id === conversationId ? 'var(--itr-surface-sunken)' : 'none',
              border: 'none',
              borderRadius: 'var(--itr-radius-sm)',
              padding: '4px 6px',
              cursor: 'pointer',
              textAlign: 'left',
              fontSize: 13,
            }}
          >
            <span className="truncate">{c.title || 'Untitled chat'}</span>
            <span className="grow" />
            <span className="subtle" style={{ fontSize: 11 }}>
              {formatRelative(c.updatedAt)}
            </span>
          </button>
        ))
      ) : (
        <span className="subtle" style={{ fontSize: 12 }}>
          No previous topics yet.
        </span>
      )}
    </div>
  );

  const messagesSection = (
    <div className="col" style={{ gap: 12 }}>
      {loadingView && messages.length === 0 ? (
        <div className="row" style={{ gap: 9, fontSize: 13, color: 'var(--itr-text-muted)' }}>
          <span className="spinner" />
          Loading…
        </div>
      ) : messages.length === 0 ? (
        <p className="subtle" style={{ fontSize: 13, lineHeight: 1.55 }}>
          {bound
            ? 'Ask about this issue — the model can pull metrics, run live checks, and (with your approval) acknowledge or resolve it.'
            : 'Start a chat. Not bound to one alert/ticket/resource — ask it to look something up across the estate.'}
        </p>
      ) : (
        messages
          .filter((m) => m.role !== 'tool')
          .map((m) =>
            m.blockType === 'tool_use' ? (
              <ToolCallEntry
                key={m.id}
                message={m}
                result={resultByToolUseId.get(m.toolUseId ?? '')}
                canDecide={m.toolName ? can(actionPermission(m.toolName)) : false}
                deciding={decidingId === m.id}
                onDecide={(decision) => decide(m.id, decision)}
              />
            ) : (
              <MessageBubble key={m.id} message={m} />
            ),
          )
      )}
    </div>
  );

  const composer = (
    <div className="col" style={{ gap: 9, flex: 1 }}>
      <textarea
        className="textarea"
        placeholder={bound ? 'Ask about this issue…' : 'Ask the AI…'}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !sending) void send();
        }}
      />
      <div className="row">
        <span className="grow" />
        <button className="btn btn-primary" onClick={send} disabled={sending || !input.trim()}>
          {sending ? <span className="spinner" style={{ width: 13, height: 13 }} /> : <Send size={14} />}
          {sending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <button className="btn" style={{ marginBottom: 14 }} onClick={() => setExpanded(true)}>
        <MessageSquareText size={14} />
        AI chat
        {view && (
          <span className="subtle" style={{ fontSize: 11 }}>
            · {formatRelative(view.conversation.updatedAt)}
          </span>
        )}
      </button>

      {expanded && (
        <Drawer
          open
          onClose={() => setExpanded(false)}
          wide
          footer={configured ? composer : undefined}
          title={
            <div className="row" style={{ gap: 8 }}>
              <MessageSquareText size={14} style={{ color: 'var(--itr-primary)' }} />
              <span>AI chat</span>
              <span className="grow" />
              {configured && (
                <>
                  <button className="btn btn-ghost btn-icon" onClick={startNewTopic} aria-label="New topic" title="New topic">
                    <Plus size={14} />
                  </button>
                  <button
                    className="btn btn-ghost btn-icon"
                    onClick={() => setShowHistory((v) => !v)}
                    aria-label="Toggle history"
                    title="History"
                    style={historyVisible ? { color: 'var(--itr-primary)' } : undefined}
                  >
                    <History size={14} />
                  </button>
                </>
              )}
            </div>
          }
        >
          {configured ? (
            <>
              {historySection}
              {messagesSection}
            </>
          ) : (
            <p className="subtle" style={{ fontSize: 13, lineHeight: 1.55 }}>
              Not configured. {can('settings:manage') ? (
                <>
                  Set it up under <Link to="/settings">Settings → AI assistance</Link>
                </>
              ) : (
                'Ask an administrator to set it up under Settings → AI assistance'
              )}
              .
            </p>
          )}
        </Drawer>
      )}
    </>
  );
}

/** The specific permission approving this tool's action requires — mirrors ai-actions.ts on the server. */
function actionPermission(toolName: string): 'alert:ack' | 'alert:write' | 'resource:write' | 'ticket:write' | 'tools:provision' {
  if (toolName === 'ack_alert') return 'alert:ack';
  if (toolName === 'resolve_alert') return 'alert:write';
  if (toolName === 'recheck_resource') return 'resource:write';
  if (toolName === 'run_device_command') return 'tools:provision';
  return 'ticket:write';
}

function actionLabel(m: AiMessage): string {
  const input = m.toolInput ?? {};
  switch (m.toolName) {
    case 'ack_alert':
      return `Acknowledge alert ${input.alertId ?? ''}`;
    case 'resolve_alert':
      return `Resolve alert ${input.alertId ?? ''}`;
    case 'recheck_resource':
      return `Re-check resource ${input.resourceId ?? ''}`;
    case 'comment_ticket':
      return `Comment on ticket ${input.ticketId ?? ''}`;
    case 'update_ticket_state':
      return `Change ticket ${input.ticketId ?? ''} to "${input.state ?? ''}"`;
    case 'run_device_command':
      return `Run a command on ${input.resourceId ?? ''}`;
    default:
      return m.toolName ?? 'Action';
  }
}

function MessageBubble({ message }: { message: AiMessage }) {
  const isUser = message.role === 'user';
  return (
    <div
      style={{
        padding: '10px 12px',
        borderRadius: 'var(--itr-radius-sm)',
        background: isUser ? 'color-mix(in srgb, var(--itr-primary) 8%, var(--itr-surface-sunken))' : 'var(--itr-surface-sunken)',
        alignSelf: isUser ? 'flex-end' : 'stretch',
      }}
    >
      <div className="row" style={{ gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 11.5, fontWeight: 600 }}>{isUser ? 'You' : 'AI'}</span>
        <span className="grow" />
        <span className="subtle" style={{ fontSize: 11 }}>
          {formatRelative(message.createdAt)}
        </span>
      </div>
      <div style={{ whiteSpace: 'pre-wrap', fontSize: 13.5, lineHeight: 1.55 }}>{message.text}</div>
    </div>
  );
}

function ToolCallEntry({
  message,
  result,
  canDecide,
  deciding,
  onDecide,
}: {
  message: AiMessage;
  result: AiMessage | undefined;
  canDecide: boolean;
  deciding: boolean;
  onDecide: (decision: 'approve' | 'deny') => void;
}) {
  const [open, setOpen] = useState(false);

  if (message.actionStatus === 'pending') {
    return (
      <div
        style={{
          padding: '10px 12px',
          borderRadius: 'var(--itr-radius-sm)',
          background: 'color-mix(in srgb, var(--itr-warning) 10%, var(--itr-surface-sunken))',
          border: 'var(--itr-border-w) dashed color-mix(in srgb, var(--itr-warning) 45%, transparent)',
        }}
      >
        <div className="row" style={{ gap: 8, marginBottom: 8 }}>
          <ShieldAlert size={13} style={{ color: 'var(--itr-warning)' }} />
          <span style={{ fontSize: 13, fontWeight: 570 }}>{actionLabel(message)}</span>
        </div>
        {message.toolName === 'run_device_command' && (
          <div
            className="mono"
            style={{
              padding: '6px 8px',
              marginBottom: 8,
              borderRadius: 'var(--itr-radius-sm)',
              background: 'var(--itr-surface-sunken)',
              fontSize: 12.5,
              wordBreak: 'break-all',
            }}
          >
            {String(message.toolInput?.command ?? '')}
          </div>
        )}
        {!canDecide && (
          <div className="subtle" style={{ fontSize: 11.5, marginBottom: 8 }}>
            You don't have permission to approve this — someone else will need to.
          </div>
        )}
        <div className="row" style={{ gap: 8 }}>
          <button className="btn btn-sm btn-primary" disabled={!canDecide || deciding} onClick={() => onDecide('approve')}>
            <Check size={13} /> Approve
          </button>
          <button className="btn btn-sm" disabled={!canDecide || deciding} onClick={() => onDecide('deny')}>
            <X size={13} /> Deny
          </button>
        </div>
      </div>
    );
  }

  // A resolved action (approved/denied/executed/failed) still shows as a small status line.
  if (message.actionStatus && message.actionStatus !== 'auto') {
    const ok = message.actionStatus === 'executed';
    const denied = message.actionStatus === 'denied';
    return (
      <div className="row" style={{ gap: 7, fontSize: 12, color: ok ? 'var(--itr-up)' : denied ? 'var(--itr-text-subtle)' : 'var(--itr-critical)' }}>
        {ok ? <Check size={12} /> : <ShieldAlert size={12} />}
        <span>
          {actionLabel(message)} — {message.actionStatus}
          {result?.toolResult ? `: ${result.toolResult}` : ''}
        </span>
      </div>
    );
  }

  // run_sibling_device_command executed with no approval step. The tool is
  // gone — every device command now goes through run_device_command's
  // approval — but conversations from before still contain its calls, so they
  // keep their own always-open card: an operator reading back sees at a
  // glance that a device was touched without anyone signing off on it.
  if (message.toolName === 'run_sibling_device_command') {
    let parsed: { ok?: boolean; output?: string; error?: string } | null = null;
    try {
      parsed = result?.toolResult ? JSON.parse(result.toolResult) : null;
    } catch {
      parsed = null;
    }
    const failed = Boolean(parsed?.error);
    const color = failed ? 'var(--itr-critical)' : 'var(--itr-warning)';
    return (
      <div
        style={{
          padding: '10px 12px',
          borderRadius: 'var(--itr-radius-sm)',
          background: `color-mix(in srgb, ${color} 8%, var(--itr-surface-sunken))`,
          border: `var(--itr-border-w) dashed color-mix(in srgb, ${color} 45%, transparent)`,
        }}
      >
        <div className="row" style={{ gap: 8, marginBottom: 8 }}>
          <ShieldAlert size={13} style={{ color }} />
          <span style={{ fontSize: 13, fontWeight: 570 }}>
            Ran automatically on {String(message.toolInput?.resourceId ?? 'a sibling resource')} — no approval required
          </span>
        </div>
        <div
          className="mono"
          style={{
            padding: '6px 8px',
            marginBottom: 8,
            borderRadius: 'var(--itr-radius-sm)',
            background: 'var(--itr-surface-sunken)',
            fontSize: 12.5,
            wordBreak: 'break-all',
          }}
        >
          {String(message.toolInput?.command ?? '')}
        </div>
        <div className="mono subtle" style={{ fontSize: 11.5, wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
          {parsed?.error ?? parsed?.output ?? result?.toolResult ?? '(pending)'}
        </div>
      </div>
    );
  }

  // A read-only tool call, folded into a collapsible trace line like AiAnalysisPanel's.
  return (
    <div>
      <button
        className="row"
        onClick={() => setOpen((v) => !v)}
        style={{ gap: 5, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--itr-text-subtle)', fontSize: 12 }}
      >
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        <Wrench size={12} />
        {message.toolName}
      </button>
      {open && (
        <div
          style={{
            marginTop: 6,
            padding: '8px 10px',
            borderRadius: 'var(--itr-radius-sm)',
            background: 'var(--itr-surface-sunken)',
            fontSize: 11.5,
          }}
        >
          <div className="mono subtle truncate">{JSON.stringify(message.toolInput ?? {})}</div>
          <div className="mono subtle" style={{ marginTop: 4, wordBreak: 'break-all', lineHeight: 1.5 }}>
            {result?.toolResult ?? '(pending)'}
          </div>
        </div>
      )}
    </div>
  );
}
